import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      // First check if we have a session token in localStorage (email/password login)
      const sessionToken = localStorage.getItem('session_token');
      const storedUser = localStorage.getItem('user');
      
      if (sessionToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsProfileComplete(Boolean(userData?.is_profile_complete || userData?.profile_complete));
          setLoading(false);
          return;
        } catch (e) {
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
        }
      }

      // Fall back to cookie-based auth (Google OAuth)
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsProfileComplete(Boolean(userData?.is_profile_complete));
      } else {
        setUser(null);
        setIsProfileComplete(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsProfileComplete(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Login with Google OAuth (original method)
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Login with email/password - sets user directly
  const login = (userData) => {
    if (userData && typeof userData === 'object') {
      // Email/password login - user data passed directly
      setUser(userData);
      setIsProfileComplete(Boolean(userData?.is_profile_complete || userData?.profile_complete));
      // Redirect based on role
      const roleRedirects = {
        'master_admin': '/admin-dashboard',
        'admin': '/admin-dashboard',
        'compras': '/compras',
        'produccion': '/produccion',
        'proveedor': '/proveedor',
        'cliente_empresa': '/empresa',
        'cliente_comprador': '/cliente'
      };
      const redirectPath = roleRedirects[userData.role] || '/menu';
      window.location.href = redirectPath;
    }
    // If no userData, do nothing - don't auto-redirect to Google OAuth
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Clear localStorage tokens
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, loginWithGoogle, logout, checkAuth, isProfileComplete }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
